import {
  Component,
  Input,
  HostBinding,
  ViewChild,
  HostListener,
  OnInit,
  SecurityContext
} from '@angular/core';
import * as prism from 'prismjs';
import 'prismjs/components/prism-markup-templating.js';
import 'prismjs/components/prism-typescript';
import 'prismjs/components/prism-php';
import 'prismjs/components/prism-ruby';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-java';
import 'prismjs/components/prism-markdown';
import 'prismjs/components/prism-scss';
import 'prismjs/components/prism-swift';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

export enum SnippetType {
  single = 'single',
  multi = 'multi',
  inline = 'inline'
}

export enum SnippetLanguage {
  javascript  = 'javascript',
  json        = 'json',
  typescript  = 'typescript',
  markup      = 'markup',
  markdown    = 'markdown',
  php         = 'php',
  ruby        = 'ruby',
  scss        = 'scss',
  css         = 'css',
  html        = 'html',
  xml         = 'xml',
  clike       = 'clike',
  java        = 'java',
  swift       = 'swift',
}

@Component({
  selector: 'suka-code-snippet',
  template: `
    <ng-container *ngIf="display === 'inline'; else notInline">
      <span
        sukaTooltip="Copiado"
        tooltipTrigger="click"
        tooltipDuration="3000"
      >
        <ng-container *ngTemplateOutlet="codeTemplate"></ng-container>
      </span>
    </ng-container>

    <ng-template #notInline>
      <div class="snippet-container" attr.aria-label="Copy code snippet">
        <ng-container *ngIf="skeleton">
          <span *ngIf="display === 'single'; else multiSkeleton"></span>
          <ng-template #multiSkeleton>
            <span></span>
            <span></span>
            <span></span>
          </ng-template>
        </ng-container>
        <pre *ngIf="!skeleton"><ng-container *ngTemplateOutlet="codeTemplate"></ng-container></pre>
      </div>

      <button
        *ngIf="!skeleton"
        class="snippet-button"
        attr.aria-label="Copiar"
        (click)="onCopyButtonClicked()"
        [ngClass]="{
          'snippet-button--copied': showFeedback
        }"
        sukaTooltip="Copiado"
        tooltipTrigger="click"
        tooltipDuration="3000"
        tabindex="0">
        <suka-icon icon="copy"></suka-icon>
      </button>

      <button
        *ngIf="display === 'multi' && shouldShowExpandButton"
        class="btn snippet-btn--expand"
        (click)="toggleSnippetExpansion()"
        type="button">
        <span class="snippet-btn--text">{{expanded ? 'Mostrar menos' : 'Mostrar mas'}}
          <suka-icon icon="chevron-down"></suka-icon>
        </span>
      </button>
    </ng-template>
    <ng-template #codeTemplate>
      <code #code [innerHtml]="codeHtml"></code>
    </ng-template>
  `
})
// tslint:disable-next-line: component-class-suffix
export class CodeSnippet implements OnInit {
  /**
   * Variable used for creating unique ids for code-snippet components.
   */
  static codeSnippetCount = 0;
  public codeHtml: SafeHtml;

  /**
   * It can be `"single"`, `"multi"` or `"inline"`
   */
  @Input() display: SnippetType = SnippetType.single;
  /**
   * Text displayed in the tooltip when user clicks button to copy code.
   *
   */
  @Input() feedbackText = 'Copiado'; // TODO: i18n

  /**
   * Code content
   *
   */
  @Input() content: string;

  /**
   * Code highlight
   *
   */
  @Input() highlight: false;

  /**
   * Snippet highlight language
   *
   */
  @Input() language: SnippetLanguage = SnippetLanguage.javascript;

  /**
   * Time in miliseconds to keep the feedback tooltip displayed.
   *
   */
  @Input() feedbackTimeout = 3000;

  /**
   * Set to `true` to show an expanded code snippet.
   */
  @HostBinding('class.snippet--expand') @Input() expanded = false;

  /**
   * Set to `true` to show a loading code snippet.
   */
  @HostBinding('class.skeleton') @Input() skeleton = false;

  @HostBinding('class.snippet') snippetClass = true;
  @HostBinding('class.snippet--single') get snippetSingleClass() {
    return this.display === SnippetType.single;
  }
  @HostBinding('class.snippet--multi') get snippetMultiClass() {
    return this.display === SnippetType.multi;
  }
  @HostBinding('class.snippet--inline') get snippetInlineClass() {
    return this.display === SnippetType.inline;
  }
  @HostBinding('class.btn--copy') get btnCopyClass() {
    return this.display === SnippetType.inline;
  }

  @HostBinding('style.display') get displayStyle() {
    return this.display !== SnippetType.inline ? 'block' : null;
  }
  @HostBinding('attr.type') get attrType() {
    return this.display === SnippetType.inline ? 'button' : null;
  }

  @ViewChild('code', { static: false }) code;

  get shouldShowExpandButton() {
    return this.code ? this.code.nativeElement.getBoundingClientRect().height > 255 : false;
  }

  showFeedback = false;

  /**
   * Creates an instance of CodeSnippet.
   */
  constructor(private sanitizer: DomSanitizer) {
    CodeSnippet.codeSnippetCount++;
  }

  ngOnInit() {
    if (this.highlight) {
      this.content = prism.highlight(this.content, prism.languages[this.language], this.language);
    }

    this.codeHtml = this.sanitizer.sanitize(SecurityContext.HTML, this.content);
  }

  toggleSnippetExpansion() {
    this.expanded = !this.expanded;
  }

  /**
   * Copies the code from the `<code>` block to clipboard.
   */
  public copyCode() {
    // create invisible, uneditable textarea with our code in it
    const textarea = document.createElement('textarea');
    textarea.value = this.code.nativeElement.innerText || this.code.nativeElement.textContent;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.right = '-99999px';
    document.body.appendChild(textarea);

    // save user selection
    const selected = document.getSelection().rangeCount ? document.getSelection().getRangeAt(0) : null;

    // copy to clipboard
    textarea.select();
    document.execCommand('copy');

    // remove textarea
    document.body.removeChild(textarea);

    // restore user selection
    if (selected) {
      document.getSelection().removeAllRanges();
      document.getSelection().addRange(selected);
    }
  }

  /**
   * On copy button click, copies the code and shows feedback.
   */
  onCopyButtonClicked() {
    this.copyCode();

    this.showFeedback = true;

    setTimeout(() => {
      this.showFeedback = false;
    }, this.feedbackTimeout);
  }

  /**
   * Inline code snippet acts as button and makes the whole component clickable.
   *
   * This handles clicks in that case.
   */
  @HostListener('click')
  hostClick() {
    if (this.display !== SnippetType.inline) {
      return;
    }

    this.onCopyButtonClicked();
  }
}
